import { Button, Carousel, Link, Spinner, Text } from "@ttc3k/trekker";
import {
  AssetImage,
  EventCheckoutWidget,
  EventWithOccurrences,
} from "gql/generated";
import { useCallback, useMemo } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudinaryImage } from "shared/components/atoms/CloudinaryImage";
import { SmartImage } from "shared/components/atoms";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import { isEventCancelledForOrganization } from "shared/utils/eventHelpers";
import { css } from "styled-system/css";
import { Box, BoxProps, Center, FlexProps } from "styled-system/jsx";
import { Flex } from "styled-system/jsx";
import { EventSchedule } from "widgets/events/components/EventSchedule";
import { WhereaboutsLogo } from "shared/components/atoms";
import _ from "lodash";
import { MultilingualLangKey } from "shared/types";
import { Locale } from "date-fns";
import { i18nLanguage } from "i18n";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { formatPhoneNumber, getFormattedDetailsLink } from "shared/utils";
import { BookerIFrame } from "widgets/booker/features/BookerIFrame";
import { CheckoutLauncher } from "shared/components/organisms/CheckoutLauncher";
import { EventInfo } from "./EventInfo";
import { EventMapAndCards } from "./EventMapAndCards";
import { EventContactInfo } from "./EventContactInfo";

type EventWidgetEventDetailsProps = {
  event: EventWithOccurrences & {
    checkoutWidgets?: Array<EventCheckoutWidget | null> | null;
  };
  lang: MultilingualLangKey;
  locale: Locale | undefined;
  contentBoxProps?: FlexProps;
  carouselBoxProps?: BoxProps;
  eventDetailsLink?: string;
  organizationId: string;
  widgetId?: string;
  viewOnly?: boolean;
  enableNonOperatorWarning: boolean;
  listingHeroFallbackImage?: Partial<AssetImage> | null;
};

/** `MultilingualLangKey` and `i18nLanguage` share the same string values ("en"/"fr"/"es"). */
const toI18nLanguage = (lang: MultilingualLangKey): i18nLanguage =>
  i18nLanguage[lang] ?? i18nLanguage.en;

export const EventWidgetEventDetails = ({
  event,
  lang,
  locale,
  contentBoxProps,
  carouselBoxProps,
  eventDetailsLink,
  organizationId,
  widgetId,
  viewOnly,
  enableNonOperatorWarning = false,
  listingHeroFallbackImage,
}: EventWidgetEventDetailsProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });
  const trackPostHogEvent = useWidgetTrack();

  /**
   * When the screen is < 768px in width, the image is full width
   * - In these cases, we need to account for a possible scrollbar (e.g. mobile vs desktop)
   *   and use the scrollWidth of the main flex contianer as the image width to fetch
   *   from Cloudinary
   * - Unfortunately, that main flex container isn't drawn until the operator details are
   *   found, so need to run this code in a timeout
   */
  const [imageWidth, setImageWidth] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  // Recursive function to wait for the UI to draw
  const checkScrollWidth = useCallback(() => {
    if (contentRef.current) {
      setImageWidth(contentRef.current.scrollWidth);
    } else {
      setTimeout(() => checkScrollWidth(), 1);
    }
  }, [setImageWidth]);

  // Start the size checking when the operator changes
  useEffect(() => {
    setImageWidth(0);
    setTimeout(() => checkScrollWidth(), 1);
  }, [checkScrollWidth, event]);

  const description = getMultilingualString(event?.description ?? {});
  const eventImages = _.get(event, "images", []) as Partial<AssetImage>[];
  const images =
    eventImages.length > 0
      ? eventImages
      : listingHeroFallbackImage
        ? [listingHeroFallbackImage]
        : [];

  // Handle the click event for the event details link (open in new tab)
  const handleEventDetailsLinkClick = () => {
    if (!viewOnly) {
      trackPostHogEvent(PostHogCustomEvent.ExpandedDetailsClick, {
        organizationId: organizationId,
        widgetId: widgetId,
        eventId: event._id,
      });
    }
    window.open(
      getFormattedDetailsLink(
        getMultilingualString(event.title ?? {}, lang),
        eventDetailsLink!,
        event._id,
      ),
      "_blank",
      "noopener",
    );
  };

  // Get the event URL (from the ticketUrl if it exists), phone, and email from the event organizer
  const { eventUrl, eventPhone, eventEmail } = useMemo(() => {
    return {
      eventUrl:
        (event.ticketUrl || event.eventOrganizer.contact?.links?.website) ?? "",
      eventPhone: formatPhoneNumber(event.eventOrganizer.contact?.phone ?? ""),
      eventEmail: event.eventOrganizer.contact?.email ?? "",
    };
  }, [event]);

  // Open a window and optionally track the event with PostHog
  const handleWindowOpen = (url: string, trackWithPostHog: boolean = false) => {
    if (!viewOnly && trackWithPostHog) {
      trackPostHogEvent(PostHogCustomEvent.OutboundClick, {
        organizationId: organizationId,
        widgetId: widgetId,
      });
    }
    window.open(url, "_blank", "noopener");
  };

  // Publicly published checkout widgets for this event (opted in via the "Sharing" step)
  const checkoutWidgets = useMemo(
    () =>
      (event.checkoutWidgets ?? []).filter(
        (widget): widget is EventCheckoutWidget => !!widget,
      ),
    [event.checkoutWidgets],
  );
  const isCancelled = isEventCancelledForOrganization(event, organizationId);
  // Buy Tickets takes precedence over the external ticketUrl link, and is hidden
  // for cancelled events. It remains visible (but untracked) in view-only
  // (dashboard preview) mode so operators can test the checkout flow before publishing.
  const showBuyTickets = !isCancelled && checkoutWidgets.length > 0;

  const [isCheckoutLauncherOpen, setIsCheckoutLauncherOpen] = useState(false);
  const [activeCheckoutBookerId, setActiveCheckoutBookerId] = useState<
    string | null
  >(null);

  const handleBuyTicketsClick = useCallback(() => {
    if (!viewOnly) {
      trackPostHogEvent(PostHogCustomEvent.BuyTicketsCheckoutClick, {
        organizationId,
        widgetId,
        eventId: event._id,
        widgetCount: checkoutWidgets.length,
      });
    }
    if (checkoutWidgets.length === 1) {
      setActiveCheckoutBookerId(checkoutWidgets[0].bookerId);
    } else {
      setIsCheckoutLauncherOpen(true);
    }
  }, [
    checkoutWidgets,
    event._id,
    organizationId,
    trackPostHogEvent,
    viewOnly,
    widgetId,
  ]);

  const handleCheckoutLauncherLaunch = useCallback(
    (bookerId: string) => {
      if (!viewOnly) {
        trackPostHogEvent(PostHogCustomEvent.BuyTicketsCheckoutClick, {
          organizationId,
          widgetId,
          eventId: event._id,
          bookerId,
        });
      }
      setIsCheckoutLauncherOpen(false);
      setActiveCheckoutBookerId(bookerId);
    },
    [event._id, organizationId, trackPostHogEvent, viewOnly, widgetId],
  );

  return (
    <Flex flexDir={"column"} h={"100%"} ref={contentRef} {...contentBoxProps}>
      <Box position="sticky" top={0} h="400px" {...carouselBoxProps}>
        {images.length > 0 && imageWidth > 0 ? (
          <Carousel
            images={images}
            controlClassName={css({ pb: "0" })}
            tagClassName={css({ pb: "0" })}
            render={(image: Partial<AssetImage>) => (
              <Flex
                alignItems="center"
                justifyContent="center"
                h="400px"
              >
                <SmartImage
                  image={image}
                  width={imageWidth || 465}
                  height={400}
                  shape="square"
                  previewVideo={true}
                />
              </Flex>
            )}
          />
        ) : images.length === 0 ? (
          <CloudinaryImage
            imageStyleProps={{
              h: "400px",
              borderStyle: "none",
              borderColor: "transparent",
              borderRadius: "0",
              aspectRatio: `${imageWidth} : 400`,
              width: "full",
            }}
          />
        ) : (
          <Center h="400px" w="full">
            <Spinner width={"40px"} height={"40px"} />
          </Center>
        )}
      </Box>
      <Flex
        part={"details__main"}
        flexDir={"column"}
        flexGrow={1}
        px={"500"}
        py="600"
        bg={"bg.lightest"}
        zIndex={1}
        gap={"400"}
      >
        {/* Event Organizer, URL, Phone, Email, and Description */}
        <EventInfo
          showNonOperatorWarning={enableNonOperatorWarning}
          isCancelled={isEventCancelledForOrganization(event, organizationId)}
          lang={lang}
          title={getMultilingualString(event.title, lang)}
          eventDetailsLink={eventDetailsLink}
          handleEventDetailsLinkClick={handleEventDetailsLinkClick}
          eventOrganizer={event.eventOrganizer}
          eventUrl={eventUrl}
          eventPhone={eventPhone}
          eventEmail={eventEmail}
          handleWindowOpen={handleWindowOpen}
          description={description}
        />

        {(event?.price || event.ticketUrl || showBuyTickets) && (
          <Flex part="details__price-container" flexDir="column" gap="100">
            <Text part="details__price-title" visual="smallLabel">
              {t("LABELS.PRICE")}
            </Text>
            {event?.price && (
              <Text
                part="details__price"
                visual={"bodyRegular"}
                color={"text.mid"}
                whiteSpace="pre-wrap"
              >
                {event.price}
              </Text>
            )}
            {/* Buy Tickets (native checkout) takes precedence over the external ticketUrl link */}
            {showBuyTickets ? (
              <Button
                part="details__buy-tickets"
                size="sm"
                alignSelf="flex-start"
                onClick={handleBuyTicketsClick}
              >
                {t("BUTTONS.BUY_TICKETS")}
              </Button>
            ) : (
              event.ticketUrl && (
                <Link
                  part="details__price-more-info-link"
                  href={event.ticketUrl}
                  onClick={() =>
                    handleWindowOpen(event.ticketUrl as string, true)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  color="#0066CC"
                  alignSelf="flex-start"
                  textAlign="left"
                  _hover={{ textDecoration: "underline" }}
                >
                  {t("LABELS.CLICK_HERE_FOR_MORE_INFO")}
                </Link>
              )
            )}
          </Flex>
        )}

        {/* Event Location */}
        {event.locationType === "IN_PERSON" && (
          <EventMapAndCards
            eventLocations={event.eventLocations ?? []}
            lang={lang}
            showNonOperatorWarning={enableNonOperatorWarning}
            handleWindowOpen={handleWindowOpen}
          />
        )}

        {/* Schedule */}
        <EventSchedule event={event} locale={locale} />

        {/* Repeat contact info */}
        {(eventUrl || eventPhone || eventEmail) && (
          <EventContactInfo
            eventUrl={eventUrl}
            handleWindowOpen={handleWindowOpen}
            eventPhone={eventPhone}
            eventEmail={eventEmail}
          />
        )}

        <Link
          href={"https://www.whereabouts.tech/"}
          display={"flex"}
          flexDir="column"
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
          gap="75"
          userSelect={"none"}
          rel="noopener noreferrer"
          target="_blank"
          color={"text.dark"}
        >
          <Text visual={"smallTag"} opacity={0.5}>
            {t("WORDS.POWERED_BY")}
          </Text>
          <WhereaboutsLogo width={100} height={"auto"} />
        </Link>
      </Flex>

      {checkoutWidgets.length > 1 && (
        <CheckoutLauncher
          open={isCheckoutLauncherOpen}
          onClose={() => setIsCheckoutLauncherOpen(false)}
          widgets={checkoutWidgets}
          lang={lang}
          onLaunch={handleCheckoutLauncherLaunch}
        />
      )}

      {/* Preload each published widget's checkout iframe so it's ready by the time it's opened */}
      {checkoutWidgets.map(widget => (
        <BookerIFrame
          key={widget.bookerId}
          bookerId={widget.bookerId}
          open={activeCheckoutBookerId === widget.bookerId}
          onClose={() => setActiveCheckoutBookerId(null)}
          bookerPosition="right"
          language={toI18nLanguage(lang)}
        />
      ))}
    </Flex>
  );
};
